import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, TextField, Switch, FormControlLabel, InputAdornment,
  Typography, Box, useTheme, alpha,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { FeeCategory, FeeCategoryInput } from '../../types/fees';
import feeCategoryService from '../../services/feeCategoryService';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import LedgerAccountField, { accountChange } from '../finance/ledger/LedgerAccountField';

interface FeeCategoryDialogProps {
  open: boolean;
  editing: FeeCategory | null;
  onClose: () => void;
  onSaved: () => void;
}

const slug = (v: string) =>
  v.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const validationSchema = yup.object({
  name: yup.string().trim().required('Name is required').max(100),
  code: yup
    .string()
    .trim()
    .required('Code is required')
    .max(60)
    .matches(/^[a-z0-9_]+$/, 'Lowercase letters, digits, and underscores only'),
  description: yup.string().trim().max(500),
  taxRate: yup
    .number()
    .transform((v, orig) => (orig === '' || orig === null ? null : v))
    .nullable()
    .min(0, 'Must be between 0 and 100')
    .max(100, 'Must be between 0 and 100'),
  displayOrder: yup.number().integer('Must be a whole number').min(0),
});

const FeeCategoryDialog: React.FC<FeeCategoryDialogProps> = ({ open, editing, onClose, onSaved }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const { canWrite } = useModulePermissions();

  const initialValues = useMemo(() => ({
    name: editing?.name ?? '',
    code: editing?.code ?? '',
    description: editing?.description ?? '',
    isOptional: editing?.isOptional ?? false,
    isRefundable: editing?.isRefundable ?? true,
    taxRate: editing?.taxRate != null ? Math.round(editing.taxRate * 10000) / 100 : '', // stored 0..1, shown as %
    displayOrder: editing?.displayOrder ?? 0,
    glAccountCode: editing?.glAccountCode ?? '',
  }), [editing]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload: FeeCategoryInput = {
          name: values.name.trim(),
          code: values.code.trim().toLowerCase(),
          description: values.description?.trim() || undefined,
          isOptional: values.isOptional,
          isRefundable: values.isRefundable,
          taxRate: values.taxRate === '' || values.taxRate == null
            ? null
            : Number(values.taxRate) / 100,   // % → 0..1
          displayOrder: Number(values.displayOrder) || 0,
          ...accountChange(canWrite('finance_accounts'), values.glAccountCode, initialValues.glAccountCode),
        };
        if (editing) {
          await feeCategoryService.updateFeeCategory(editing._id, payload);
          enqueueSnackbar(`Updated “${payload.name}”`, { variant: 'success' });
        } else {
          await feeCategoryService.createFeeCategory(payload);
          enqueueSnackbar(`Created “${payload.name}”`, { variant: 'success' });
        }
        onSaved();
        onClose();
      } catch (error: any) {
        enqueueSnackbar(error?.message || 'Could not save fee category', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });


  // Auto-suggest a code from the name until the admin types in the code field.
  // Only kicks in on Create, not Edit — editing a name shouldn't quietly rename
  // the machine key.
  useEffect(() => {
    if (editing) return;
    if (codeManuallyEdited) return;
    formik.setFieldValue('code', slug(formik.values.name), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.name, editing, codeManuallyEdited]);

  useEffect(() => {
    if (open) setCodeManuallyEdited(Boolean(editing));
  }, [open, editing]);

  return (
    <Dialog open={open} onClose={formik.isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {editing ? 'Edit fee category' : 'Add fee category'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Fee categories are the line items that make up every fee plan and invoice.
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                name="name"
                label="Name"
                placeholder="e.g. Tuition"
                fullWidth
                autoFocus
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
              <TextField
                name="code"
                label="Code"
                placeholder="tuition"
                fullWidth
                value={formik.values.code}
                onChange={(e) => {
                  setCodeManuallyEdited(true);
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.code && Boolean(formik.errors.code)}
                helperText={(formik.touched.code && formik.errors.code) || 'Machine key — stays stable if the name changes'}
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Stack>

            <TextField
              name="description"
              label="Description"
              placeholder="Optional — shown on the challan line and in reports"
              fullWidth
              multiline
              minRows={2}
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />

            <LedgerAccountField
              use="fee"
              value={formik.values.glAccountCode}
              onChange={v => formik.setFieldValue('glAccountCode', v)}
              suggestedCode={editing?.suggestedAccountCode}
              help="Where this fee is recorded in the books. Leave empty to use the suggestion. Security deposits belong in a liability."
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                name="taxRate"
                label="Tax rate"
                placeholder="0"
                fullWidth
                type="number"
                value={formik.values.taxRate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.taxRate && Boolean(formik.errors.taxRate)}
                helperText={(formik.touched.taxRate && formik.errors.taxRate) || 'Reserved — will be applied when tax logic ships'}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
              />
              <TextField
                name="displayOrder"
                label="Display order"
                fullWidth
                type="number"
                value={formik.values.displayOrder}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.displayOrder && Boolean(formik.errors.displayOrder)}
                helperText={(formik.touched.displayOrder && formik.errors.displayOrder) || 'Lower numbers appear first'}
                inputProps={{ min: 0, step: 1 }}
              />
            </Stack>

            <Box sx={{
              p: 1.5, borderRadius: 1.5,
              bgcolor: alpha(theme.palette.text.primary, 0.03),
              border: `1px solid ${theme.palette.divider}`,
            }}>
              <FormControlLabel
                control={<Switch checked={formik.values.isOptional} onChange={(_, v) => formik.setFieldValue('isOptional', v)} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Optional</Typography>
                    <Typography variant="caption" color="text.secondary">Students may opt out (e.g. transport, hostel)</Typography>
                  </Box>
                }
                sx={{ display: 'flex', mb: 0.5 }}
              />
              <FormControlLabel
                control={<Switch checked={formik.values.isRefundable} onChange={(_, v) => formik.setFieldValue('isRefundable', v)} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Refundable</Typography>
                    <Typography variant="caption" color="text.secondary">Uncheck for one-time non-returnable fees (e.g. admission fee)</Typography>
                  </Box>
                }
                sx={{ display: 'flex' }}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} disabled={formik.isSubmitting}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? 'Saving…' : editing ? 'Save changes' : 'Create category'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FeeCategoryDialog;
